import React from 'react';
import { TableRow, TableCell } from '@mui/material';
import { Table, TableWrapper, TableHeadCell, TableCheckboxActions } from '../../design-system';

type SimpleTableProps<T extends { data: unknown[]; totalCount: number }> = {
  id?: string;
  data: T;
  filters?: null | (() => React.ReactNode);
  checkboxActions?: (data: T['data']) => React.ReactNode;
  tableRowProps: (
    item: T['data'][number],
    i: number,
  ) => { key: string | number; onClick: () => void };
  columns: {
    column?: number;
    head?: () => React.ReactNode;
    headText?: () => string;
    body?: (item: T['data'][number], i: number) => React.ReactNode;
    bodyText?: (item: T['data'][number], i: number) => string | number;
    isHidden?: (item: T['data'][number], i: number) => boolean;
  }[];
  footer?: () => React.ReactNode;
};

export const SimpleTable = <T extends { data: any[]; totalCount: number }>({
  id,
  data: queryData,
  filters,
  checkboxActions,
  tableRowProps,
  columns,
  footer,
}: SimpleTableProps<T>) => {
  const { data } = queryData;
  const filteredColumns = columns.filter((column, i) =>
    data.some((item) => !column.isHidden?.(item, i)),
  );

  const tableColumns = filteredColumns.map((item) => item.column || null);

  return (
    <TableWrapper id={id} columns={tableColumns} simpleAutoColumn>
      {!!filters && filters()}
      <TableCheckboxActions withoutSpacing={!filters}>
        {!!checkboxActions && checkboxActions(data)}
      </TableCheckboxActions>
      <Table
        sx={{ tableLayout: 'auto', width: 'auto', minWidth: '100%' }}
        headType={'light'}
        bodyRowSize={'small'}
        showLastRowBorder={!!footer}
        renderTableHeadRow={
          <TableRow>
            {filteredColumns.map((column, i) => {
              if (column.head) {
                return <React.Fragment key={`head-${i}`}>{column.head()}</React.Fragment>;
              }

              return (
                <TableHeadCell key={`head-${i}`} align={'left'}>
                  {column.headText?.()}
                </TableHeadCell>
              );
            })}
          </TableRow>
        }
        renderTableBodyRows={data.map((item, i) => (
          <TableRow {...tableRowProps(item, i)} key={tableRowProps(item, i).key}>
            {filteredColumns.map((column, n) => {
              if (column.body) {
                return (
                  <React.Fragment key={`body-${i}-${n}`}>{column.body(item, i)}</React.Fragment>
                );
              }

              return (
                <TableCell key={`body-${i}-${n}`} align={'left'}>
                  {column.bodyText?.(item, i)}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      />
      {footer ? footer() : <span />}
    </TableWrapper>
  );
};
